import * as XLSX from 'xlsx';
import type { ExportOrder } from './exportOrderShape';
import { sortOrdersForExport } from './parxlCsvExport';

const SENDER = {
  name: 'Kuen Park',
  company: 'SkinSeoul',
  street: '490, Jungbong-daero, Seo-gu',
  city: 'Incheon',
  province: 'Incheon',
  postalCode: '22770',
  country: 'KR',
  telephone: '+82 01074308430',
};

const FIXED_ITEM_WEIGHT_KG = 0.2;
const HS_CODE = '3304.99';
const ROUTING_CODE = 'KRMUZXR';

const BASE_HEADERS = [
  'CustomerOrderNo.',
  'RoutingCode',
  'Trackingnumber',
  'AdditionalServices',
  'VatNumber',
  'EoriNumber',
  'IossCode',
  'CountryCode',
  'Name',
  'CertificateCode',
  'Company',
  'Street',
  'City',
  'Province/State',
  'ZipCode',
  'phone',
  'HouseNumber',
  'Email',
  'PackageNumber',
  'PackageWeight',
  'SenderFiastName',
  'SenderCompany',
  'SenderStreet',
  'SenderCity',
  'SenderProvince',
  'SenderPostalCode',
  'SenderCountry',
  'SenderTelephone',
  'CurrencyCode',
];

const ITEM_FIELDS = [
  'SKU',
  'ItemDescription',
  'ForeignItemDescription',
  'DeclaredQuantity',
  'UnitPrice',
  'UnitWeight',
  'HsCode',
  'Remarks',
  'SalesLink',
  'Materials',
  'Use',
  'Brand',
  'ModelType',
  'Specs',
];

/** Header fill colors matching the standard YUN Express template */
type CellStyle = { fill: { fgColor: { rgb: string } } };
const YELLOW_FILL: CellStyle = { fill: { fgColor: { rgb: 'FFFF00' } } };
const RED_FILL: CellStyle = { fill: { fgColor: { rgb: 'FF0000' } } };
const ORANGE_FILL: CellStyle = { fill: { fgColor: { rgb: 'FFC000' } } };

/** Map of base header index -> fill style (matching the standard template) */
const HEADER_FILLS: Record<number, CellStyle> = {
  0: YELLOW_FILL,   // CustomerOrderNo.
  2: RED_FILL,      // Trackingnumber
  3: ORANGE_FILL,   // AdditionalServices
  4: ORANGE_FILL,   // VatNumber
  5: ORANGE_FILL,   // EoriNumber
  6: ORANGE_FILL,   // IossCode
  15: YELLOW_FILL,  // phone
};

function getMaxItemCount(orders: ExportOrder[]): number {
  let max = 1;
  for (const order of orders) {
    const count = order.lineItems?.length || 0;
    if (count > max) max = count;
  }
  return max;
}

function buildHeaders(maxItems: number): string[] {
  const headers = [...BASE_HEADERS];
  for (let i = 1; i <= maxItems; i++) {
    for (const field of ITEM_FIELDS) {
      headers.push(`${field}${i}`);
    }
  }
  return headers;
}

type CellValue = string | number | null;

function orderToYunRow(order: ExportOrder, maxItems: number): CellValue[] {
  const shipping = order.shippingAddress;
  const customer = order.customer;
  const consignee = order.consignee;
  const lineItems = order.lineItems || [];
  const totalQuantity = lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  const name = consignee?.name || [shipping?.firstName, shipping?.lastName].filter(Boolean).join(' ') || '';
  const street = [shipping?.address1, shipping?.address2].filter(Boolean).join(' ') || consignee?.address || '';

  const row: CellValue[] = [
    order.orderId || '',
    ROUTING_CODE,
    order.shippingData?.trackingNumber || order.shippingData?.providerOrderId || null, // Trackingnumber
    null, // AdditionalServices
    null, // VatNumber
    null, // EoriNumber
    null, // IossCode
    consignee?.countryCode || shipping?.country || '',
    name,
    null, // CertificateCode
    shipping?.company || '',
    street,
    shipping?.city || consignee?.city || '',
    shipping?.state || consignee?.state || '',
    shipping?.postcode || consignee?.zipcode || '',
    shipping?.phone || customer?.phone || '',
    null, // HouseNumber
    shipping?.email || customer?.email || '',
    1, // PackageNumber
    parseFloat((FIXED_ITEM_WEIGHT_KG * totalQuantity).toFixed(2)),
    SENDER.name,
    SENDER.company,
    SENDER.street,
    SENDER.city,
    SENDER.province,
    SENDER.postalCode,
    SENDER.country,
    SENDER.telephone,
    order.currency || 'USD',
  ];

  for (let i = 0; i < maxItems; i++) {
    const item = lineItems[i];
    if (item) {
      row.push(
        item.sku || '',
        item.nameKor || '',
        item.name || '',
        item.quantity || 0,
        item.price || 0,
        FIXED_ITEM_WEIGHT_KG,
        HS_CODE,
        null, // Remarks
        null, // SalesLink
        null, // Materials
        null, // Use
        null, // Brand
        null, // ModelType
        null, // Specs
      );
    } else {
      for (let j = 0; j < ITEM_FIELDS.length; j++) {
        row.push(null);
      }
    }
  }

  return row;
}

function isPrepareShipment(order: ExportOrder): boolean {
  const status = (order.status || '').toLowerCase();
  return status === 'prepare-shipment' || status === 'prepare_shipment';
}

export function filterYunOrders(orders: ExportOrder[]): ExportOrder[] {
  return orders.filter(
    (order) =>
      isPrepareShipment(order) &&
      order.shippingData?.provider === 'YUN_EXPRESS',
  );
}

export function getYunOrderCount(orders: ExportOrder[]): number {
  return filterYunOrders(orders).length;
}

export function downloadYunExpressExcel(orders: ExportOrder[], startDate: string, endDate: string): void {
  writeYunExpressExcel(filterYunOrders(orders), startDate, endDate);
}

/**
 * Writes the given orders as the standard template without filtering them, for a caller whose own
 * status vocabulary picks the parcels (Fulfillment 2.0 has no `prepare-shipment`).
 */
export function writeYunExpressExcel(orders: ExportOrder[], startDate: string, endDate: string): void {
  const sortedOrders = sortOrdersForExport(orders);

  const maxItems = getMaxItemCount(sortedOrders);
  const headers = buildHeaders(maxItems);

  const data: (CellValue | null)[][] = [headers, ...sortedOrders.map((order) => orderToYunRow(order, maxItems))];

  const ws = XLSX.utils.aoa_to_sheet(data);

  // Apply header fill colors from the standard template
  for (let colIdx = 0; colIdx < headers.length; colIdx++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
    if (!ws[cellRef]) ws[cellRef] = { v: headers[colIdx], t: 's' };
    const fill = HEADER_FILLS[colIdx];
    if (fill) {
      ws[cellRef].s = fill;
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '표준탬플릿');

  XLSX.writeFile(wb, `yun_express_${startDate}_${endDate}.xlsx`);
}
